import type { HttpErrorResponse } from '@angular/common/http';
import { isArray, isDate, isEmpty, isNil, isNumber, isObject, isString } from 'lodash-es';
import moment from 'moment';

import { CHAT_ROLE, type InteractionAdapter } from '@app/shared/components/chat/model/chat.model';
import {
    type EXECUTION_TYPE,
    EXECUTION_TYPE_SHORT_CODE_MAPPER,
    type MODULES,
    OBJECT_INFO,
    OBJECTS,
    type PAGES,
    type STATUS_ALIAS,
    STATUS_COLOR_CODE,
    STATUS_SHORT_CODE,
    type WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import type { InputResponseModel } from '@app/shared/models/input-response.model';
import type { NotificationAdapter } from '@app/shared/models/notification.model';
import { TruncatePipe } from '@app/shared/pipes/truncate.pipe';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import type { RouteInfo } from '@app/shared/utils/router-helper';

export class Helpers {
    static getErrorMessageString(errorResponse: HttpErrorResponse): string {
        // Fetch the information for Errors other than schema errors (eg: timeout-issue)
        // {"error": {"message": "<String Error Message>", "code" "<API CODE>" ...}}
        return errorResponse?.error?.error?.message || '';
    }

    /**
     * Concat module/object name with pageName/action
     * @returns concatenated string
     */
    static getPermissionName(
        moduleOrObject: MODULES | OBJECTS,
        actionOrPage: PAGES | string,
    ): string {
        return `${moduleOrObject}.${actionOrPage}`;
    }

    static isValidDate(date: string, format: string): boolean {
        return moment(date, format, true).isValid();
    }

    /**
     * Adds the styling classes and shortcode
     */
    static addStylingToInput<
        T extends {
            objectType?: OBJECTS;
            currentStatus?: WORKFLOW_STATUS;
            executionType?: EXECUTION_TYPE;
        },
    >(
        item: T,
    ): T & {
        statusClass?: string;
        textColor?: string;
        statusShortcut?: STATUS_ALIAS | string;
    } {
        const additionalConfig: {
            textColor?: string;
            statusClass?: string;
            statusShortcut?: STATUS_ALIAS | string;
            statusShortcutTooltip?: string;
        } = {};
        if (item.executionType) {
            additionalConfig.statusClass = 'cor--status-tag cor--status-execution-tag';
            additionalConfig.statusShortcut = EXECUTION_TYPE_SHORT_CODE_MAPPER[item.executionType];
            additionalConfig.statusShortcutTooltip = item.executionType;
            additionalConfig.textColor = '#333';
        } else {
            if (item.currentStatus) {
                additionalConfig.statusClass = `cor--status-tag ${STATUS_COLOR_CODE[item.currentStatus]}`;
                additionalConfig.statusShortcut = STATUS_SHORT_CODE[item.currentStatus];
                additionalConfig.statusShortcutTooltip = item.currentStatus;
            }
            additionalConfig.textColor = OBJECT_INFO[item.objectType]?.color || 'black';
        }

        return { ...item, ...additionalConfig };
    }

    static getObjectWithName(objects: readonly OBJECTS[]) {
        return objects.map((obj: OBJECTS) => {
            return {
                displayName: OBJECT_INFO[obj].name,
                name: obj,
            };
        });
    }

    /**
     *
     * @param value Helper function to check whether the number, string, object
     *  exists or not
     * Checks for null/undefined/number/string/array/object
     * isEmpty(0) => False
     * isEmpty('') => True
     * isEmpty({}) => True
     * @returns Boolean
     */
    static isEmpty(value: number | string | object | File) {
        if (isNil(value)) {
            return true; // Return true for undefined and null
        }

        if (isNumber(value)) {
            return false; // Numbers are considered non-empty
        }

        if (isDate(value)) {
            return false;
        }

        // Check specifically for the File type
        if (value instanceof File) {
            return value.size === 0;
        }

        if (isString(value) || isArray(value) || isObject(value)) {
            return isEmpty(value); // Check if string, array, or object is empty using lodash's isEmpty
        }

        return false; // Return false for other value types
    }

    /**
     * Helps you to stringify the non-empty objects present in LoadParams for Get request
     * Note:- we should not stringify arrays
     */
    static parseLoadOptionsForGet(loadOptions: any) {
        delete loadOptions.userData;
        Object.keys(loadOptions).forEach(key => {
            if (
                loadOptions[key] &&
                TypeHelpers.isObject(loadOptions[key]) &&
                Object.keys(loadOptions[key]).length > 0 &&
                !(
                    ['string', 'boolean'].includes(typeof loadOptions[key]) ||
                    (Array.isArray(loadOptions[key]) && loadOptions[key].length > 0)
                )
            ) {
                loadOptions[key] = JSON.stringify(loadOptions[key]);
            }
        });

        return loadOptions;
    }

    /**
     * Helps you to stringify the non-empty objects present in LoadParams
     */
    static parseLoadOptions(loadOptions: any) {
        delete loadOptions.userData;
        Object.keys(loadOptions).forEach(key => {
            if (
                loadOptions[key] &&
                TypeHelpers.isObject(loadOptions[key]) &&
                Object.keys(loadOptions[key]).length > 0 &&
                !['string', 'boolean'].includes(typeof loadOptions[key])
            ) {
                loadOptions[key] = JSON.stringify(loadOptions[key]);
            }
        });

        return loadOptions;
    }

    /**
     * Helps you create a placeholder/default name value to use when it is not provided/set by user
     */
    static getPlaceholderName(prefix: string, date?: string) {
        const dateStr = moment
            .utc(date || new Date())
            .local()
            .format('MMM-DD-YYYY hh:mm A'); // Output e.g.: simulation_Oct-05-2020 09:46 PM

        return `${prefix}: ${dateStr}`.trim();
    }

    /**
     * @deprecated We should stop using this function, and move to directly using RouterHelper.buildRoute()
     * This function was the older way of handling URLs when we had fragemented approaches to generate and
     * create links
     */
    static convertNotificationDataToUIRouteSchema(
        data: NotificationAdapter['messageData'],
    ): RouteInfo {
        const routeInfo: RouteInfo = {
            object: data.targetType, // always required
            id: data.targetId, // always required
            projectSlug: data.projectSlug,
            simulationId: data?.actionObjectId, // can be empty
            tabRoutes: Object.values(TAB_ROUTES).includes(data?.actionObjectType)
                ? data?.actionObjectType
                : undefined, // can be empty
        };

        if (
            data.verb === 'Assigned' &&
            data.targetType === OBJECTS.ANNOTATION_QUEUE &&
            (!isNil(data.rowId) || !isNil(data.rowIndex))
        ) {
            routeInfo.queryParams = {
                tab: 'labeling',
                ...(!isNil(data.rowId)
                    ? { rowId: data.rowId }
                    : { rowIndex: String(data.rowIndex) }),
            };
        }

        return routeInfo;
    }

    /**
     * @deprecated We should stop using this function, and move to directly using RouterHelper.buildRoute()
     * This function was the older way of handling URLs when we had fragemented approaches to generate and
     * create links
     */
    static convertInputResponseModelToUIRouteSchema(data: InputResponseModel): RouteInfo {
        if (data.executionType) {
            // executionType is present for `simulation` InputResponseModel -> treat it differently from an object
            return {
                object: data.objectType,
                id: data.entityId,
                projectSlug: data.projectSlug,
                simulationId: data.id,
            };
        }

        return {
            object: data.objectType, // always required
            id: data.id, // always required
            projectSlug: data.projectSlug,
        };
    }

    /**
     * converts MB to bytes
     * @param megabytes is a number
     * @returns number too
     */
    static megabytesToBytes(megabytes: number): number {
        return isNumber(megabytes) ? megabytes * 1024 * 1024 : undefined;
    }

    /**
     * converts bytes to MB
     * @param bytes is a number
     * @returns number too
     */
    static bytesToMegaBytes(bytes: number): number {
        return isNumber(bytes) ? bytes / (1024 * 1024) : undefined;
    }

    /**
     * This function takes in user's name and generate capitalized initials from it.
     *
     * ex: John Doe => JD
     *     john doe => JD
     *     John Jack Doe => JJ
     *     John => J
     */
    static getInitialsFromName(name: string): string {
        return name
            .split(' ')
            .map(x => x.charAt(0))
            .join('')
            .substring(0, 2)
            .toUpperCase();
    }

    /**
     * Resolves a CSS variable to its computed value so JS consumers like Plotly receive a real color.
     */
    static resolveCssVar(variableName: string, root: Element | null): string {
        if (!root) return '';
        return window.getComputedStyle(root).getPropertyValue(variableName).trim();
    }
}

export class TypeHelpers {
    /**
     * Helper function to check if a provided value is string or not.
     * It also handles typing so typescript can understand this check
     */
    static isString(value: any): value is string {
        return typeof value === 'string';
    }

    /**
     * Helper function to check if a provided value is boolean or not.
     * It also handles typing so typescript can understand this check
     */
    static isBoolean(value: any): value is boolean {
        return typeof value === 'boolean';
    }

    /**
     * Helper function to check if a provided value is an object or not.
     * It also handles typing so typescript can understand this check
     */
    // FIXME: The prefer-restricted-member rule detects this as a private variable because
    //        it is only used in this file. But instead of checking the file, we should actually
    //        be checking whether the member is being used in this particular object

    static isObject(v: any): v is object {
        return v && typeof v === 'object';
    }
}

/**
 * Used in place of @Input({ transform: numberAttribute }) arg: number;
 * Returns number if the number is valid, else returns undefined
 * This should only be used for the input transforms in components
 */
export function parseAsNumber(value: unknown): number {
    // parseFloat(value) handles most of the cases we're interested in (it treats null, empty string,
    // and other non-number values as NaN, where Number just uses 0) but it considers the string
    // '123hello' to be a valid number. Therefore we also check if Number(value) is NaN.
    const isNumberValue =
        !Number.isNaN(Number.parseFloat(value as any)) && !Number.isNaN(Number(value));

    return isNumberValue ? Number(value) : undefined;
}

export function parseChatMessage(chatMessagesRaw: string): {
    errorMessage?: string;
    interactions?: InteractionAdapter[];
    rawMessage: string;
} {
    if (!chatMessagesRaw) return { rawMessage: chatMessagesRaw };

    // We currently allow interactions here to be string or InteractionContent for backward compatibility
    // The string value is added in the AQ parquet files already. So, avoiding a data migration for now.
    let parsed: (InteractionAdapter & { content: string })[];
    try {
        parsed = JSON.parse(chatMessagesRaw);
    } catch (error) {
        let reason = 'Unknown error while parsing JSON.';
        if (error instanceof SyntaxError) {
            reason = `Invalid JSON syntax: ${error.message}`;
        }

        return {
            errorMessage: `Failed to parse chat messages.\n${reason}\nEnsure the content is valid JSON.`,
            rawMessage: chatMessagesRaw,
        };
    }

    if (!Array.isArray(parsed)) {
        return {
            errorMessage:
                'Invalid format: Expected an array of chat messages but received a different structure.',
            rawMessage: chatMessagesRaw,
        };
    }
    const invalidItems = parsed
        .map((item, iitem) => {
            if (!isObject(item)) {
                return `Invalid item at item ${iitem + 1}: expected an object with keys "role", "content", "tool_calls", "tool_call_id"`;
            }

            const errors = [];
            if (!Object.values(CHAT_ROLE).includes(item.role)) {
                errors.push(
                    `Invalid role at item ${iitem + 1}: ${item.role} - expected (${Object.values(CHAT_ROLE).join(', ')})`,
                );
            }
            if (
                !isNil(item.content) &&
                !Array.isArray(item.content) &&
                !(typeof item.content === 'string')
            ) {
                const content = TruncatePipe.truncate(JSON.stringify(item.content), 50);
                errors.push(
                    `Invalid content at item ${iitem + 1}: ${content} - expected a string, or an OpenAI-style content array or null`,
                );
            }
            return errors.join('\n');
        })
        .filter(x => !!x);

    if (invalidItems.length > 0) {
        return {
            errorMessage: invalidItems.join('\n'),
            rawMessage: chatMessagesRaw,
        };
    }

    return {
        interactions: parsed.map(
            (interaction, index): InteractionAdapter => ({
                ...interaction,
                content:
                    typeof interaction.content === 'string'
                        ? [{ type: 'text', text: interaction.content }]
                        : interaction.content,
                id: interaction.id ?? index, // NOTE: if id is not present add index as id, since copy message and some other functionality needs id
            }),
        ),
        rawMessage: chatMessagesRaw,
    };
}
